// Sidebar.js
import React, {useState, useEffect} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react'
import {
  Bars3Icon,
  DocumentDuplicateIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  KeyIcon,
  UserIcon,
  DocumentChartBarIcon,
  UserGroupIcon,
  ChartBarIcon,
  ChartPieIcon,
  ChartBarSquareIcon,
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  DocumentMinusIcon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline'
import { BASE_URL } from '../Shared/Constants';


// Configuración de navegación organizada por secciones
const navigationSections = [
  {
    id: 'main',
    title: '',
    items: [
      { name: 'Inicio', href: '#', icon: HomeIcon, to: BASE_URL + '/' },
    ]
  },
  {
    id: 'users',
    title: 'Gestión de Usuarios',
    items: [
      { name: 'Grupos y Permisos', href: '#', icon: KeyIcon, to: BASE_URL + '/rules' },
      { name: 'Usuarios del Sistema', href: '#', icon: UsersIcon, to: BASE_URL + '/users' },
      { name: 'Funcionarios Policiales', href: '#', icon: UserIcon, to: BASE_URL + '/officials' },
      { name: 'Historial de Personas', href: '', icon: UserGroupIcon, to: BASE_URL + '/users/penalities' }
    ]
  },
  {
    id: 'penalties',
    title: 'Administración de Multas',
    items: [
      { name: 'Gestión de Multas', href: '#', icon: DocumentDuplicateIcon, to: BASE_URL + '/penalities' },
      { name: 'Multas por Funcionario', href: '#', icon: ClipboardDocumentCheckIcon, to: BASE_URL + '/official-fines' },
      { name: 'Validación de Pagos', href: '#', icon: BanknotesIcon, to: BASE_URL + '/payment-validation' },
      { name: 'Conceptos', href: '#', icon: FolderIcon, to: BASE_URL + '/concepts' },
      { name: 'Factor de Multa', href: '#', icon: DocumentChartBarIcon, to: BASE_URL + '/factor' },
      { name: 'Revocaciones', href: '#', icon: DocumentMinusIcon, to: BASE_URL + '/revocations' },
    ]
  },
  {
    id: 'reports',
    title: 'Reportes y Análisis',
    items: [
      { name: 'Auditoría', href: '#', icon: ChartBarIcon, to: BASE_URL + '/audit' },
      { name: 'Tesorería', href: '#', icon: ChartPieIcon, to: BASE_URL + '/treasure' },
      { name: 'Métricas', href: '#', icon: ChartBarSquareIcon, to: BASE_URL + '/metrics' }
    ]
  }
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Componente para el logo y header corporativo
const SidebarHeader = () => (
  <div className="flex items-center px-6 py-6 border-b border-gray-700">
    <div className="flex items-center">
      <div className="flex-shrink-0">
        <BuildingOffice2Icon className="h-8 w-8 text-indigo-400" />
      </div>
      <div className="ml-3">
        <h1 className="text-lg font-bold text-white">Admininistrador</h1>
        <p className="text-xs text-gray-400">Version 1.0.0 </p>
      </div>
    </div>
  </div>
)

// Componente para el perfil de usuario
const UserProfile = ({ userName, userType }) => (
  <div className="px-6 py-4 border-b border-gray-700">
    <div className="flex items-center">
      <img
        alt="Avatar"
        src="https://pbs.twimg.com/profile_images/1346257565605367811/afnuY09l_400x400.jpg"
        className="h-10 w-10 rounded-full bg-gray-600 ring-2 ring-gray-500"
      />
      <div className="ml-3 flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">{userName}</p>
        <p className="text-xs text-gray-400 truncate">{userType}</p>
      </div>
    </div>
  </div>
)

// Componente para elementos de navegación
const NavigationItem = ({ item, isActive, onCloseSidebar }) => (
  <li>
    <Link
      to={item.to}
      onClick={onCloseSidebar}
      className={classNames(
        isActive
          ? 'bg-indigo-700 text-white border-r-2 border-indigo-400'
          : 'text-gray-300 hover:bg-gray-700 hover:text-white',
        'group flex items-center px-3 py-2.5 text-sm font-medium rounded-l-md transition-all duration-200 ease-in-out'
      )}
    >
      <item.icon 
        onClick={onCloseSidebar}
        className={classNames(
          isActive ? 'text-indigo-300' : 'text-gray-400 group-hover:text-gray-300',
          'mr-3 h-5 w-5 flex-shrink-0 cursor-pointer'
        )} 
        aria-hidden="true" 
      />
      <span className="truncate cursor-pointer" onClick={onCloseSidebar}>{item.name}</span>
    </Link>
  </li>
)

// Componente para secciones de navegación
const NavigationSection = ({ section, currentPath, onCloseSidebar }) => (
  <div className="space-y-1">
    {section.title && (
      <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
        {section.title}
      </h3>
    )}
    <ul className="space-y-1">
      {section.items.map((item) => (
        <NavigationItem
          onClick={onCloseSidebar}
          key={item.name}
          item={item}
          isActive={currentPath === item.to}
          onCloseSidebar={onCloseSidebar}
        />
      ))}
    </ul>
  </div>
)



const SidebarComponent = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const location = useLocation()
    
    // Obtener información del usuario
    const userName = window.localStorage.getItem('userName') || 'Usuario'
    const userType = window.localStorage.getItem('userType') || 'Administrador'
    const userIdType = window.localStorage.getItem('idType')
    
    // Función de logout mejorada
    const logout = () => {
        const confirmLogout = window.confirm('¿Está seguro que desea cerrar sesión?')
        if (confirmLogout) {
            window.localStorage.removeItem('token')
            window.localStorage.removeItem('permissons')
            window.localStorage.removeItem('userType')
            window.localStorage.removeItem('userName')
            window.localStorage.removeItem('idType')
            window.location.href=window.location.origin+'/login'
        }
    }

    // Agregar llamado de atención para super usuarios
    const getNavigationSections = () => {
        const sections = [...navigationSections]
        if (userIdType === '99') {
            const penaltiesSection = sections.find(section => section.id === 'penalties')
            if (penaltiesSection && !penaltiesSection.items.find(item => item.name === 'Llamado de Atención')) {
                penaltiesSection.items.push({
                    name: 'Llamado de Atención',
                    href: '#',
                    icon: DocumentChartBarIcon,
                    to: BASE_URL + '/nullations'
                })
            }
        }
        return sections
    }

    const currentPath = location.pathname


    return (
      <div>
        {/* Sidebar móvil modernizado */}
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button 
                    type="button" 
                    onClick={() => setSidebarOpen(false)} 
                    className="-m-2.5 p-2.5 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors duration-200"
                  >
                    <span className="sr-only">Cerrar sidebar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              
              {/* Contenido del sidebar móvil */}
              <div className="flex grow flex-col overflow-y-auto bg-gray-900 shadow-xl">
                {/* Header */}
                <SidebarHeader />
                
                {/* Perfil de usuario */}
                <UserProfile userName={userName} userType={userType} />
                
                {/* Navegación */}
                <nav className="flex-1 px-4 py-6 space-y-8">
                  {getNavigationSections().map((section) => (
                    <NavigationSection
                      key={section.id}
                      section={section}
                      currentPath={currentPath}
                      onCloseSidebar={() => setSidebarOpen(false)}
                    />
                  ))}
                </nav>
                
                {/* Botón de logout */}
                <div className="px-4 py-4 border-t border-gray-700">
                  <button
                    onClick={logout}
                    className="w-full flex items-center px-3 py-2.5 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white transition-all duration-200"
                  >
                    <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Sidebar de escritorio modernizado */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          <div className="flex grow flex-col overflow-y-auto bg-gray-900 shadow-xl">
            {/* Header corporativo */}
            <SidebarHeader />
            
            {/* Perfil de usuario */}
            <UserProfile userName={userName} userType={userType} />
            
            {/* Navegación principal */}
            <nav className="flex-1 px-4 py-6 space-y-8">
              {getNavigationSections().map((section) => (
                <NavigationSection
                  key={section.id}
                  section={section}
                  currentPath={currentPath}
                  onCloseSidebar={() => {}} // No hace nada en desktop
                />
              ))}
            </nav>
            
            {/* Footer con botón de logout */}
            <div className="px-4 py-4 border-t border-gray-700">
              <button
                onClick={logout}
                className="w-full flex items-center px-3 py-2.5 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white transition-all duration-200 group"
              >
                <ArrowRightOnRectangleIcon 
                  className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" 
                  aria-hidden="true" 
                />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>

        {/* Barra superior móvil modernizada */}
        <div className="sticky top-0 z-40 flex items-center justify-between bg-gray-900 px-4 py-4 shadow-lg border-b border-gray-700 lg:hidden">
          <div className="flex items-center">
            <button 
              type="button" 
              onClick={() => setSidebarOpen(true)} 
              className="p-2.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors duration-200"
            >
              <span className="sr-only">Abrir menú</span>
              <Bars3Icon aria-hidden="true" className="h-6 w-6" />
            </button>
            <div className="ml-4 flex items-center">
              <BuildingOffice2Icon className="h-6 w-6 text-indigo-400 mr-2" />
              <div>
                <h1 className="text-sm font-bold text-white">Admin Multas</h1>
                <p className="text-xs text-gray-400">Chacao</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center">
            <img
              alt="Avatar"
              src="https://pbs.twimg.com/profile_images/1346257565605367811/afnuY09l_400x400.jpg"
              className="h-8 w-8 rounded-full bg-gray-600 ring-2 ring-gray-500"
            />
            <div className="ml-2 hidden sm:block">
              <p className="text-xs font-medium text-white truncate">{userName}</p>
              <p className="text-xs text-gray-400 truncate">{userType}</p>
            </div>
          </div>
        </div>
      </div>
    );
};

export default SidebarComponent;